import { useCallback, useEffect, RefObject } from 'react'

type PositionCallback = (component: HTMLElement, elementBounds: DOMRect) => void

interface UseFloatPositioningProps {
  isOpen: boolean
  ref: RefObject<HTMLElement | null>
  relatedToRef: RefObject<HTMLElement | null>
  onOpen?: PositionCallback
  onPositionTop?: PositionCallback
  onPositionBottom?: PositionCallback
  onPositionRight?: PositionCallback
  onPositionLeft?: PositionCallback
  noRepositionTop?: boolean
}

export const useFloatPositioning = ({
  isOpen,
  ref,
  relatedToRef,
  onOpen,
  onPositionTop,
  onPositionBottom,
  onPositionRight,
  onPositionLeft,
  noRepositionTop,
}: UseFloatPositioningProps): void => {
  const handlePositioning = useCallback(() => {
    if (isOpen && ref.current && relatedToRef.current) {
      const component = ref.current
      const elementBounds = relatedToRef.current.getBoundingClientRect()
      const right =
        window.innerWidth - elementBounds.right + elementBounds.width / 2

      component.style.position = 'fixed'
      component.style.transform = 'translate(50%)'
      component.style.right = `${right}px`
      component.style.top = `${elementBounds.bottom + 16}px`
      onOpen?.(component, elementBounds)

      if (right + component.offsetWidth / 2 > window.innerWidth) {
        component.style.transform = `translateX(calc(50% + ${
          right + component.offsetWidth / 2 - window.innerWidth
        }px))`
        onPositionRight?.(component, elementBounds)
      }

      if (window.innerWidth - elementBounds.right < component.offsetWidth / 2) {
        component.style.transform = `translateX(${
          elementBounds.width / 2 + window.innerWidth - elementBounds.right
        }px)`
        onPositionLeft?.(component, elementBounds)
      }

      if (
        window.innerHeight - elementBounds.bottom >
          component.offsetHeight + 16 ||
        elementBounds.top < component.offsetHeight + 16
      ) {
        onPositionBottom?.(component, elementBounds)
        component.style.top = `${elementBounds.bottom + 16}px`
      } else if (!noRepositionTop) {
        onPositionTop?.(component, elementBounds)
        component.style.top = 'unset'
        component.style.bottom = `${
          window.innerHeight - elementBounds.top + 16
        }px`
      }
    }
  }, [isOpen])

  useEffect(() => {
    handlePositioning()

    document.addEventListener('wheel', handlePositioning)

    return () => {
      document.removeEventListener('wheel', handlePositioning)
    }
  }, [handlePositioning])
}

const CURSOR_OFFSET = 16
const VIEWPORT_PADDING = 8

/**
 * Positions a floating element next to the mouse pointer, flipping to the
 * opposite side of the cursor when it would otherwise overflow the viewport.
 */
export const positionAtCursor = (
  component: HTMLElement,
  x: number,
  y: number,
): void => {
  component.style.position = 'fixed'
  component.style.transform = 'none'
  component.style.right = 'auto'
  component.style.bottom = 'auto'

  let left = x + CURSOR_OFFSET
  let top = y + CURSOR_OFFSET

  if (left + component.offsetWidth + VIEWPORT_PADDING > window.innerWidth) {
    left = x - component.offsetWidth - CURSOR_OFFSET
  }

  if (top + component.offsetHeight + VIEWPORT_PADDING > window.innerHeight) {
    top = y - component.offsetHeight - CURSOR_OFFSET
  }

  component.style.left = `${Math.max(VIEWPORT_PADDING, left)}px`
  component.style.top = `${Math.max(VIEWPORT_PADDING, top)}px`
}
